import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../config/firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";

function TeacherDashboard() {
  const [isTeacher, setIsTeacher] = useState(false);
  const [checking, setChecking] = useState(true);
  const [studentId, setStudentId] = useState("");
  const [student, setStudent] = useState(null);
  const [msg, setMsg] = useState("");
  const [updateFields, setUpdateFields] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/teacher-login");
        return;
      }
      // Check if user is a teacher
      const q = query(collection(db, "teachers"), where("uid", "==", user.uid));
      const snap = await getDocs(q);
      setIsTeacher(!snap.empty);
      setChecking(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  // Fetch student details by Firestore document ID
  const fetchStudent = async (e) => {
    e.preventDefault();
    setMsg("");
    setStudent(null);
    if (!studentId) return;
    try {
      const q = query(
        collection(db, "students"),
        where("uid", "==", studentId)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const docSnap = snap.docs[0];
        setStudent({ id: docSnap.id, ...docSnap.data() });
        setUpdateFields({});
      } else {
        setMsg("No student found with this UID.");
      }
    } catch (err) {
      setMsg("Error fetching student: " + err.message);
    }
  };
  // Handle update input changes
  const handleUpdateChange = (e) => {
    setUpdateFields({ ...updateFields, [e.target.name]: e.target.value });
  };

  // Update student details
  const updateStudent = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      const docRef = doc(db, "students", student.id);
      await updateDoc(docRef, updateFields);
      setMsg("Student updated successfully!");
      // Refresh student data
      const docSnap = await getDoc(docRef);
      setStudent({ id: docSnap.id, ...docSnap.data() });
      setUpdateFields({});
    } catch (err) {
      setMsg("Error updating student: " + err.message);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#16355C]">
        <div className="text-white text-xl">Checking permissions...</div>
      </div>
    );
  }

  if (!isTeacher) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#16355C]">
        <div className="bg-white p-8 rounded shadow text-center">
          <div className="text-xl font-bold text-[#16355C] mb-2">
            Access Denied
          </div>
          <div className="text-gray-700">
            You must be a teacher to access this page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#16355C]">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-lg">
        <h2
          className="text-2xl font-bold text-center mb-6"
          style={{ color: "#16355C" }}
        >
          Teacher Dashboard
        </h2>
        <form onSubmit={fetchStudent} className="mb-6 flex gap-2">
          <input
            type="text"
            className="flex-1 border rounded px-3 py-2"
            placeholder="Enter Student Document ID"
            value={studentId}
            onChange={(e) => setStudentId(e.target.value)}
            required
          />
          <button
            type="submit"
            className="bg-[#16355C] text-white px-4 py-2 rounded font-semibold hover:bg-blue-900 transition"
          >
            Fetch
          </button>
        </form>
        {msg && <div className="mb-4 text-center text-red-600">{msg}</div>}
        {student && (
          <form onSubmit={updateStudent} className="space-y-3">
            <div className="font-semibold text-[#16355C] mb-2">
              Student Details
            </div>
            {Object.entries(student).map(([key, value]) =>
              key === "id" ? null : (
                <div key={key}>
                  <label className="block text-xs font-semibold mb-1 capitalize">
                    {key}
                  </label>
                  <input
                    type="text"
                    name={key}
                    className="w-full border rounded px-3 py-2"
                    value={
                      updateFields[key] !== undefined
                        ? updateFields[key]
                        : value
                    }
                    onChange={handleUpdateChange}
                  />
                </div>
              )
            )}
            <button
              type="submit"
              className="w-full bg-[#16355C] text-white py-2 rounded font-semibold hover:bg-blue-900 transition"
            >
              Update Student
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default TeacherDashboard;
